import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/hooks/useAuth';
import { tenantApi, queryKeys } from '@/lib/api';

export function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();

  const tenantQuery = useQuery({
    queryKey: queryKeys.tenant,
    queryFn: () => tenantApi.get(),
    enabled: status === 'authenticated',
    staleTime: 60_000,
  });

  if (status === 'loading') return <FullscreenSpinner />;
  if (status === 'unauthenticated') {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/sign-in?next=${next}`} replace />;
  }

  const onOnboarding = location.pathname.startsWith('/onboarding');
  if (tenantQuery.isPending) return <FullscreenSpinner />;
  const needsOnboarding = !tenantQuery.data?.onboardedAt;
  if (needsOnboarding && !onOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }
  if (!needsOnboarding && onOnboarding) {
    return <Navigate to="/app" replace />;
  }
  return <Outlet />;
}

export function RedirectIfAuthed() {
  const { status } = useAuth();
  const tenantQuery = useQuery({
    queryKey: queryKeys.tenant,
    queryFn: () => tenantApi.get(),
    enabled: status === 'authenticated',
    staleTime: 60_000,
  });

  if (status === 'loading') return <FullscreenSpinner />;
  if (status === 'authenticated') {
    if (tenantQuery.isPending) return <FullscreenSpinner />;
    const to = tenantQuery.data?.onboardedAt ? '/app' : '/onboarding';
    return <Navigate to={to} replace />;
  }
  return <Outlet />;
}

function FullscreenSpinner() {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-canvas">
      <span
        className="h-6 w-6 animate-spin rounded-full border-2 border-line border-t-ink"
        aria-label="Loading"
      />
    </div>
  );
}
